import { roundToPlaces } from "../../utils/roundToPlaces.js";
import { Country } from "../Country.js";
import type { CountrySettings } from "../CountrySettings.js";
import type { Customer } from "../Customer.js";
import { CustomerType } from "../CustomerType.js";
import type { OrderInput } from "../Order.js";
import type { Subcategory } from "../Subcategory.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { fathersFamilyDiscountPercentage, fathersFamilyDiscountProductAmount } from "../fathersFamilyDiscount.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { getCustomerType } from "./getCustomerType.js";
import { getWeightInGramsOfOrder } from "./getWeightInGramsOfOrder.js";
import { isCountryInEuropeanUnion } from "./isCountryInEuropeanUnion.js";

interface OrderDiscountSettings {
	customer: Customer | null;
	countrySettings: CountrySettings[];
	subcategories: Pick<Subcategory, "id" | "eligibleForB2cDiscount">[];
}

interface Discounts {
	freeDelivery: boolean;
	discountsByTaxRate: TaxedPrice[];
}

export function calculateOrderDiscounts(
	order: Pick<OrderInput, "products" | "deliveryAddress">,
	{ customer, countrySettings, subcategories }: OrderDiscountSettings,
): Discounts {
	const discounts: Discounts = {
		discountsByTaxRate: [],
		freeDelivery: false,
	};

	// When we're not logged in, there's no discounts at all.
	if (!customer) {
		return discounts;
	}

	const deliverySettings = countrySettings.find(({ country }) => country === order.deliveryAddress.country)?.data
		?.delivery;
	if (!deliverySettings) {
		throw new Error(`Delivery settings not found for country "${order.deliveryAddress.country}"`);
	}

	const totalWeightInGrams = getWeightInGramsOfOrder(order);
	const customerType = getCustomerType(customer.pricingLevel);

	if (!isCountryInEuropeanUnion(order.deliveryAddress.country)) {
		return discounts;
	}

	const freeDeliveryPriceThreshold = {
		B2c: deliverySettings.freeDeliveryPriceThresholdForRegisteredB2c,
		B2b: deliverySettings.freeDeliveryPriceThresholdForRegisteredB2b,
	}[customerType];
	discounts.freeDelivery = totalWeightInGrams >= freeDeliveryPriceThreshold * 1000;

	if (customerType === CustomerType.B2c) {
		const eligibleSubcategoryIds = new Set(
			subcategories.filter(({ eligibleForB2cDiscount }) => eligibleForB2cDiscount).map(({ id }) => id),
		);
		const discountedProducts = order.products.filter((product) =>
			eligibleSubcategoryIds.has(product.info.subcategoryId),
		);
		const discountedProductsQuantity = discountedProducts.reduce((sum, product) => sum + product.quantity, 0);
		discounts.freeDelivery ||= discountedProductsQuantity >= 1;

		if (
			discountedProductsQuantity >= fathersFamilyDiscountProductAmount &&
			order.deliveryAddress.country === Country.Czechia
		) {
			const byTaxRate = new Map<number, number>();
			for (const product of discountedProducts) {
				const priceWithoutTax = byTaxRate.get(product.info.price.taxRate) ?? 0;
				byTaxRate.set(product.info.price.taxRate, priceWithoutTax + product.info.price.withoutTax * product.quantity);
			}
			for (const [taxRate, price] of byTaxRate) {
				const discountedPriceWithoutTax = roundToPlaces(price * (fathersFamilyDiscountPercentage / 100), 2);
				discounts.discountsByTaxRate.push({
					taxRate,
					withoutTax: discountedPriceWithoutTax,
					withTax: calculatePriceWithTax(discountedPriceWithoutTax, taxRate),
				});
			}
		}
	}

	return discounts;
}
